import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import SEOHead from '@/components/SEOHead';
import ProductCard from '@/components/ProductCard';
import { Search, SlidersHorizontal, X } from 'lucide-react';

export default function ProductListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);

    const categorySlug = searchParams.get('category') || '';
    const searchQuery = searchParams.get('search') || '';

    useEffect(() => {
        api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const catObj = categories.find(c => c.slug === categorySlug);
                const params = { page, limit: 20 };
                if (catObj) params.category = catObj.id;
                if (searchQuery) params.search = searchQuery;
                const res = await api.get('/products', { params });
                setProducts(res.data.products || []);
                setTotal(res.data.total || 0);
                setPages(res.data.pages || 1);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        if (categories.length > 0 || !categorySlug) load();
    }, [categorySlug, searchQuery, page, categories]);

    const currentCat = categories.find(c => c.slug === categorySlug);
    const title = currentCat ? currentCat.name : searchQuery ? `Resultados: ${searchQuery}` : 'Todos los Productos';

    const handleCategoryFilter = (slug) => {
        const p = new URLSearchParams(searchParams);
        if (slug) { p.set('category', slug); } else { p.delete('category'); }
        p.delete('search');
        setSearchParams(p);
        setPage(1);
        setFilterOpen(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const val = e.target.elements.search.value.trim();
        const p = new URLSearchParams();
        if (val) p.set('search', val);
        setSearchParams(p);
        setPage(1);
    };

    return (
        <>
            <SEOHead
                title={title}
                description={`Compra ${title.toLowerCase()} online en CloudPet. Envios a Buenos Aires.`}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="product-list-page">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-2">{title}</h1>
                    <p className="text-cp-text-body text-sm">{total} producto{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}</p>
                </div>

                <div className="flex gap-8">
                    {/* Sidebar desktop */}
                    <aside className="hidden lg:block w-56 shrink-0" data-testid="filter-sidebar">
                        <form onSubmit={handleSearch} className="mb-6">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cp-text-muted" />
                                <input
                                    name="search"
                                    defaultValue={searchQuery}
                                    placeholder="Buscar..."
                                    data-testid="filter-search-input"
                                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-cp-border rounded-xl text-sm text-cp-text-main placeholder:text-cp-text-muted"
                                />
                            </div>
                        </form>
                        <h3 className="font-heading font-bold text-cp-text-main text-sm mb-3">Categorias</h3>
                        <ul className="space-y-1">
                            <li>
                                <button
                                    onClick={() => handleCategoryFilter('')}
                                    data-testid="filter-all"
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!categorySlug ? 'bg-cp-secondary-soft text-cp-secondary font-semibold' : 'text-cp-text-body hover:bg-cp-bg-alt'}`}
                                >
                                    Todos
                                </button>
                            </li>
                            {categories.map(cat => (
                                <li key={cat.id}>
                                    <button
                                        onClick={() => handleCategoryFilter(cat.slug)}
                                        data-testid={`filter-cat-${cat.slug}`}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${categorySlug === cat.slug ? 'bg-cp-secondary-soft text-cp-secondary font-semibold' : 'text-cp-text-body hover:bg-cp-bg-alt'}`}
                                    >
                                        {cat.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </aside>

                    {/* Main */}
                    <div className="flex-1">
                        {/* Mobile filter bar */}
                        <div className="lg:hidden flex gap-2 mb-6">
                            <form onSubmit={handleSearch} className="flex-1 relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cp-text-muted" />
                                <input
                                    name="search"
                                    defaultValue={searchQuery}
                                    placeholder="Buscar productos..."
                                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-cp-border rounded-xl text-sm"
                                />
                            </form>
                            <button
                                onClick={() => setFilterOpen(!filterOpen)}
                                data-testid="mobile-filter-toggle"
                                className="px-3 py-2 bg-white border border-cp-border rounded-xl text-cp-text-body"
                            >
                                <SlidersHorizontal size={18} />
                            </button>
                        </div>

                        {/* Mobile filter dropdown */}
                        {filterOpen && (
                            <div className="lg:hidden bg-white border border-cp-border rounded-xl p-4 mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-heading font-bold text-sm">Categorias</h3>
                                    <button onClick={() => setFilterOpen(false)}><X size={16} /></button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => handleCategoryFilter('')}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${!categorySlug ? 'bg-cp-secondary text-white' : 'bg-cp-surface text-cp-text-body'}`}
                                    >
                                        Todos
                                    </button>
                                    {categories.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => handleCategoryFilter(cat.slug)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium ${categorySlug === cat.slug ? 'bg-cp-secondary text-white' : 'bg-cp-surface text-cp-text-body'}`}
                                        >
                                            {cat.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Active filter */}
                        {(categorySlug || searchQuery) && (
                            <div className="flex items-center gap-2 mb-6">
                                {categorySlug && currentCat && (
                                    <span className="inline-flex items-center gap-1 bg-cp-secondary-soft text-cp-secondary text-xs font-semibold px-3 py-1.5 rounded-full">
                                        {currentCat.name}
                                        <button onClick={() => handleCategoryFilter('')}><X size={12} /></button>
                                    </span>
                                )}
                                {searchQuery && (
                                    <span className="inline-flex items-center gap-1 bg-cp-accent text-cp-text-main text-xs font-semibold px-3 py-1.5 rounded-full">
                                        "{searchQuery}"
                                        <button onClick={() => { const p = new URLSearchParams(); setSearchParams(p); }}><X size={12} /></button>
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Products grid */}
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
                                {[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-2xl h-72 skeleton" />)}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-20">
                                <p className="text-cp-text-muted text-lg mb-2">No se encontraron productos</p>
                                <button onClick={() => { setSearchParams({}); setPage(1); }} className="text-cp-secondary text-sm font-semibold">
                                    Ver todos los productos
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6" data-testid="products-grid">
                                {products.map(p => <ProductCard key={p.id} product={p} />)}
                            </div>
                        )}

                        {/* Pagination */}
                        {pages > 1 && (
                            <div className="flex justify-center gap-2 mt-10" data-testid="pagination">
                                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`w-10 h-10 rounded-xl text-sm font-medium ${p === page ? 'bg-cp-secondary text-white' : 'bg-white border border-cp-border text-cp-text-body hover:bg-cp-bg-alt'}`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
